import { DerbyClock, type ClockFrame } from '../fixed-step';
import { DerbyRngV1 } from '../rng';
import {
  MASK_PRESSED,
  type MinigameDef,
  type MinigameSession,
  type SessionScore,
} from './framework';

export const DRILL_TICK_S = 1 / 30;
const MAX_STEPS_PER_FRAME = 10;
const EVENT_CAP = 128;

/** Facility tier evolves the course: faster ramps, tighter gaps, and
 * double hurdles at the top tiers. */
export interface GallopTierConfig {
  rampPerS: number;
  gapMinM: number;
  gapMaxM: number;
  doubleChance: number;
}

export function gallopTier(tier: number): GallopTierConfig {
  switch (Math.max(1, Math.min(4, tier))) {
    case 1:
      return { rampPerS: 0.25, gapMinM: 9, gapMaxM: 15, doubleChance: 0 };
    case 2:
      return { rampPerS: 0.28, gapMinM: 8, gapMaxM: 14, doubleChance: 0.1 };
    case 3:
      return { rampPerS: 0.31, gapMinM: 8, gapMaxM: 13, doubleChance: 0.25 };
    default:
      return { rampPerS: 0.34, gapMinM: 7, gapMaxM: 12, doubleChance: 0.35 };
  }
}

/** Gallop Drill tuning — the Speed mini-game at the Gallop Track. */
export const DRILL_TUNING = {
  baseSpeedMS: 8,
  rampPerS: 0.15,
  jumpAirS: 0.55,
  inputBufferS: 0.1,
  stumbleSlowMul: 0.5,
  stumbleRecoverS: 0.8,
  maxHits: 3,
  durationS: 15,
  firstHurdleM: 15,
  gapMinM: 9,
  gapMaxM: 15,
  courseLengthM: 300,
  comboEvery: 4,
  burstMul: 1.1,
  burstDurS: 2,
  xpBase: 8,
  xpPerScore: 1 / 18,
  xpMax: 30,
};

export type HurdleStatus = 'up' | 'cleared' | 'hit';

export interface DrillHurdle {
  xM: number;
  status: HurdleStatus;
}

export interface DrillState {
  tick: number;
  t: number;
  x: number;
  prevX: number;
  v: number;
  airborneTicks: number;
  bufferTicks: number;
  stumbleTicks: number;
  burstTicks: number;
  hits: number;
  combo: number;
  maxCombo: number;
  hurdles: DrillHurdle[];
  nextHurdle: number;
  done: boolean;
  tape: number[];
}

export type DrillEvent =
  | { kind: 'jump' }
  | { kind: 'cleared'; combo: number }
  | { kind: 'hit'; hits: number }
  | { kind: 'burst' }
  | { kind: 'done' };

export interface DrillResult {
  score: number;
  hits: number;
  maxCombo: number;
  durationS: number;
  tape: number[];
  seed: number;
}

/** Distance that reads as a flawless T1 run — the raw=1.0 reference. */
export const DRILL_PAR_M = 150;

/**
 * The Speed mini-game: auto-gallop that keeps accelerating; jump the
 * hurdles. Deterministic 30 Hz sub-sim with the same tape-replay shape
 * as the race — one code path for live play, tests, and the harness.
 */
export class DrillSim {
  readonly seed: number;
  readonly state: DrillState;

  readonly tierConfig: GallopTierConfig;
  private readonly clock = new DerbyClock(DRILL_TICK_S, MAX_STEPS_PER_FRAME);
  private readonly events: DrillEvent[] = [];
  private jumpQueued = 0;

  constructor(seed: number, tierConfig: GallopTierConfig = gallopTier(1)) {
    this.seed = seed >>> 0;
    this.tierConfig = tierConfig;
    const rng = new DerbyRngV1(this.seed).fork('hurdles');
    const hurdles: DrillHurdle[] = [];
    let x = DRILL_TUNING.firstHurdleM;
    while (x < DRILL_TUNING.courseLengthM) {
      hurdles.push({ xM: x, status: 'up' });
      if (tierConfig.doubleChance > 0 && rng.chance(tierConfig.doubleChance)) {
        x += 6.5;
        hurdles.push({ xM: x, status: 'up' });
      }
      x += rng.range(tierConfig.gapMinM, tierConfig.gapMaxM);
    }
    this.state = {
      tick: 0,
      t: 0,
      x: 0,
      prevX: 0,
      v: DRILL_TUNING.baseSpeedMS,
      airborneTicks: 0,
      bufferTicks: 0,
      stumbleTicks: 0,
      burstTicks: 0,
      hits: 0,
      combo: 0,
      maxCombo: 0,
      hurdles,
      nextHurdle: 0,
      done: false,
      tape: [],
    };
  }

  get done(): boolean {
    return this.state.done;
  }

  jump(): void {
    this.jumpQueued += 1;
  }

  /** End the session early — score stands at distance covered. */
  abort(): void {
    if (!this.state.done) this.finish();
  }

  advance(nowMs: number): ClockFrame {
    return this.clock.advance(nowMs, 1, () => this.stepOnce());
  }

  drainEvents(): DrillEvent[] {
    return this.events.splice(0, this.events.length);
  }

  stepOnce(): void {
    const state = this.state;
    const t = DRILL_TUNING;
    if (state.done) return;
    const dt = DRILL_TICK_S;

    while (this.jumpQueued > 0) {
      this.jumpQueued -= 1;
      state.tape.push(state.tick);
      this.tryJump();
    }
    // A buffered press fires the moment the horse can jump again.
    if (state.bufferTicks > 0) {
      state.bufferTicks -= 1;
      if (this.grounded()) {
        state.bufferTicks = 0;
        this.startJump();
      }
    }

    const ramp = t.baseSpeedMS + this.tierConfig.rampPerS * state.t;
    const stumble = state.stumbleTicks > 0 ? t.stumbleSlowMul : 1;
    const burst = state.burstTicks > 0 ? t.burstMul : 1;
    state.v = ramp * stumble * burst;

    state.prevX = state.x;
    state.x += state.v * dt;
    state.t += dt;

    while (state.nextHurdle < state.hurdles.length) {
      const hurdle = state.hurdles[state.nextHurdle]!;
      if (state.x < hurdle.xM) break;
      if (state.prevX < hurdle.xM) {
        if (state.airborneTicks > 0) {
          hurdle.status = 'cleared';
          state.combo += 1;
          state.maxCombo = Math.max(state.maxCombo, state.combo);
          this.pushEvent({ kind: 'cleared', combo: state.combo });
          if (state.combo % t.comboEvery === 0) {
            state.burstTicks = Math.round(t.burstDurS / dt);
            this.pushEvent({ kind: 'burst' });
          }
        } else {
          hurdle.status = 'hit';
          state.hits += 1;
          state.combo = 0;
          state.stumbleTicks = Math.round(t.stumbleRecoverS / dt);
          state.burstTicks = 0;
          this.pushEvent({ kind: 'hit', hits: state.hits });
          if (state.hits >= t.maxHits) {
            this.finish();
            return;
          }
        }
      }
      state.nextHurdle += 1;
    }

    if (state.airborneTicks > 0) state.airborneTicks -= 1;
    if (state.stumbleTicks > 0) state.stumbleTicks -= 1;
    if (state.burstTicks > 0) state.burstTicks -= 1;
    state.tick += 1;

    if (state.t >= t.durationS) this.finish();
  }

  result(): DrillResult | null {
    if (!this.state.done) return null;
    const score = Math.floor(this.state.x);
    return {
      score,
      hits: this.state.hits,
      maxCombo: this.state.maxCombo,
      durationS: this.state.t,
      tape: [...this.state.tape],
      seed: this.seed,
    };
  }

  private grounded(): boolean {
    return this.state.airborneTicks === 0 && this.state.stumbleTicks === 0;
  }

  private tryJump(): void {
    if (this.grounded()) this.startJump();
    else this.state.bufferTicks = Math.round(DRILL_TUNING.inputBufferS / DRILL_TICK_S);
  }

  private startJump(): void {
    this.state.airborneTicks = Math.round(DRILL_TUNING.jumpAirS / DRILL_TICK_S);
    this.pushEvent({ kind: 'jump' });
  }

  private finish(): void {
    this.state.done = true;
    this.pushEvent({ kind: 'done' });
  }

  private pushEvent(event: DrillEvent): void {
    if (this.events.length >= EVENT_CAP) this.events.shift();
    this.events.push(event);
  }
}

const HARD_STOP_TICKS = 30 * 60 * 5;

/** Replay a drill from its jump tape. Pure. */
export function runDrill(seed: number, tape: readonly number[]): DrillResult {
  const sim = new DrillSim(seed);
  let cursor = 0;
  while (!sim.done && sim.state.tick < HARD_STOP_TICKS) {
    while (cursor < tape.length && tape[cursor] === sim.state.tick) {
      sim.jump();
      cursor += 1;
    }
    sim.stepOnce();
  }
  sim.abort();
  return sim.result()!;
}

export type DrillPolicy = (state: DrillState) => boolean;

export function runDrillWithPolicy(seed: number, policy: DrillPolicy): DrillResult {
  const sim = new DrillSim(seed);
  while (!sim.done && sim.state.tick < HARD_STOP_TICKS) {
    if (policy(sim.state)) sim.jump();
    sim.stepOnce();
  }
  sim.abort();
  return sim.result()!;
}

/** A competent-but-not-perfect jumper for the season harness. */
export function competentDrillPolicy(state: DrillState): boolean {
  const next = state.hurdles[state.nextHurdle];
  if (!next) return false;
  if (state.airborneTicks > 0) return false;
  const dist = next.xM - state.x;
  return dist > 1 && dist < state.v * 0.3;
}

/** Framework adapter: the drill scored as a session. Scenes render from
 * `.drill`, the sim re-runs the same class from the tape. */
export class GallopSession implements MinigameSession {
  readonly drill: DrillSim;

  constructor(tier: number, seed: number) {
    this.drill = new DrillSim(seed, gallopTier(tier));
  }

  get finished(): boolean {
    return this.drill.done;
  }

  step(mask: number): void {
    if ((mask & MASK_PRESSED) !== 0) this.drill.jump();
    this.drill.stepOnce();
  }

  score(): SessionScore {
    this.drill.abort();
    const result = this.drill.result()!;
    return {
      raw: Math.max(0, Math.min(1, result.score / DRILL_PAR_M)),
      detail: `${result.score} m · best combo ×${result.maxCombo} · ${result.hits} stumbles`,
      failed: result.hits >= DRILL_TUNING.maxHits,
    };
  }
}

export const GALLOP_DEF: MinigameDef = {
  id: 'gallop',
  name: 'Gallop Drill',
  stat: 'speed',
  energyCost: 4,
  create: (tier, seed) => new GallopSession(tier, seed),
};
